import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Message } from "../../types/Agent";
import AgentInput from "./AgentInput";
import AgentMessage from "./AgentMessage";
import { ReactComponent as ErrorIconSVG } from '../../assets/images/error-icon.svg';
import Button from "../Button";
import { BACK4APP_DOT_COM_SITE_URL } from "../../settings";

const Agent = forwardRef<
  { setInputContent: (content: string) => void },
  {
    agentId: string;
    messages?: Message[];
    isSidebarOpen: boolean;
    paymentErrorMessage?: string;
    isBusy?: boolean;
    onSend: (content: string) => Promise<boolean>;
    onStop?: () => Promise<boolean>;
  }
>(({ agentId, messages, onSend, isSidebarOpen, paymentErrorMessage, isBusy, onStop }, ref) => {
  const handleSend = async (content: string) => {
    if (isBusy) return false;
    return onSend(content);
  };
  const [autoScroll, setAutoScroll] = useState(true);
  const boxRef = useRef<HTMLDivElement>(null);
  const agentInputRef = useRef<{ setContent: (content: string) => void }>(null);

  useImperativeHandle(ref, () => ({
    setInputContent: (content: string) => {
      if (agentInputRef.current) {
        agentInputRef.current.setContent(content);
      }
    }
  }));

  useEffect(
    () => {
      if (messages && autoScroll && boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
    },
    [
      messages,
      autoScroll
    ]
  );

  const onScroll = () => {
    if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) >= 0.99 * boxRef.current.scrollHeight && !autoScroll) {
      setAutoScroll(true);
    } else if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) < 0.99 * boxRef.current.scrollHeight && autoScroll) {
      setAutoScroll(false);
    }
  };

  return (
    <div className="relative h-full w-full">
      <div className="h-full w-full overflow-y-scroll glass-scroll" ref={boxRef} onScroll={onScroll}>
        {/* Maximize horizontal space for messages (no side paddings) */}
        <div className="w-full px-0 pb-[12rem] py-4">
          <AgentMessage
            message={{
              id: 'welcome',
              role: 'assistant',
              content:  `Hi, I’m your Back4App Web App Builder. If there’s no app yet I’ll create one (tell me an app name) and share a quick plan—what would you like to build?`
            }}
          />
          {messages?.map(message => <AgentMessage key={message.id} message={message} />)}
          {paymentErrorMessage && (
            <div className="grow flex flex-col items-center justify-center mt-10">
            <ErrorIconSVG />
            <div className="mt-4 font-sora font-semibold text-lg leading-[140%] text-light-grey mb-8">
              {paymentErrorMessage}
            </div>
            <Button type='submit'><a role="button" target="_blank" rel='noreferrer noopener' href={`${BACK4APP_DOT_COM_SITE_URL}/pricing/agent?appId=${agentId}&type=agent`}>Pay Now</a></Button>
          </div>
          )}
        </div>
      </div>
      {/* Hidden on mobile - mobile has its own input in MyAgents.tsx */}
      <div className="hidden lg:block">
        <AgentInput onSend={handleSend} onStop={onStop} ref={agentInputRef} disabled={isBusy} isResponding={isBusy} />
      </div>
    </div>
  )
});

export default Agent;